import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Row, Col, Button } from '../../bootstrap/index.jsx';
import { ACTION_TYPES } from './phaseReducer.js';
import ActionButtons from './ActionButtons.jsx';
import ToggleSwitch, { TextOptionElem } from '../../common/ToggleSwitch.jsx';
import Select, { ReadOnlyMultiSelectValues } from "../../common/Select.jsx";
import DateInput from '../../common/DateInput.jsx';
import FormDataCaptureList from './FormDataCaptureList.jsx';
import PreFillContextList from "./PreFillContextList.jsx";
import PhaseCollapser from './PhaseCollapser.jsx';
import { PhaseSectionElement } from './PhaseSectionElement.jsx';
import Tooltip from '../../common/Tooltip.jsx';
import FormList from './FormList.jsx';
import { buildAssigneeOptions, parseAssigneeSelection, findAssigneeDetails, toAssigneeValue } from './phaseAssignees.js';

import {
  monitoring_report_update_approval_phase_path
} from '~/routes';

const ApprovalPhaseEdit = ({report, formOptions, availableLocales, dispatch, onReportUpdate, phase, tenantConfirmEmailProcessedSub = false, ...rest }) => {
  const [isAdvancedShown, setIsAdvancedShown] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [assignees, setAssignees] = useState(phase.body?.assignees || []);
  const [automaticApproval, setAutomaticApproval] = useState((phase.body || {}).automatic_approval || false)
  const [confirmEmailProcessedSubmission, setConfirmEmailProcessedSubmission] = useState(tenantConfirmEmailProcessedSub ? true : ((phase.body || {}).confirm_email_processed_submission || false))

  const [targetedDatagrid, setTargetedDatagrid] = useState({
    id: phase.body?.targeted_datagrid_id || '',
    col: phase.body?.targeted_datagrid_col_accessor || '',
    value: phase.body?.targeted_datagrid_col_value || '',
    loading: false
  });

  const [mayEditReport, setMayEditReport] = useState((phase.body || {}).may_edit_report || false);
  const formRef = useRef(null);
  
  const onSave = (e) => {
    e.preventDefault();
    jQuery.post(monitoring_report_update_approval_phase_path({
      id: report.id,
      phase_id: phase.id,
      assignees: assignees,
      automatic_approval: automaticApproval ? "1" : "0",
      confirm_email_processed_submission: confirmEmailProcessedSubmission ? "1" : "0",
      may_edit_report: mayEditReport ? "1" : "0",
      _options: true
    }), jQuery(formRef.current).serialize(), (res) => {
      setEditMode(false);
      //onReportUpdate(res.report);
      dispatch({type: ACTION_TYPES.UPDATE_DATA, payload: {data: res.data}})
    });
  };

  const handleAssigneeSelect = (selected) => {
    setAssignees((prev) => parseAssigneeSelection(selected, prev));
  };

  const assigneeDetails = findAssigneeDetails(assignees, formOptions.available_task_assignees, formOptions.available_work_queues);

  const datagridOptions = useMemo(() => {
    return formOptions.available_schema_datagrids?.map(v => ({value: v.value, label: v.label}));
  }, []);

  const datagridColOptions = useMemo(() => {
    return formOptions.available_schema_datagrids?.find(v => v.value == targetedDatagrid.id)?.cols || [];
  }, [targetedDatagrid.id]);

  const targetedDatagridCol = useMemo(() => {
    return datagridColOptions.find(d => d['value'] == targetedDatagrid.col);
  }, [targetedDatagrid.id, targetedDatagrid.col]);

  const handleTargetedDatagridChange = (key, value) => {
    setTargetedDatagrid((preVal) => ({...preVal, [key]: value, loading: true}));
  };

  useEffect(() => {
    if (targetedDatagrid.loading) setTargetedDatagrid((preVal) => ({...preVal, loading: false}));
  }, [targetedDatagrid.loading]);

  return <>
    <form ref={formRef}>
      <Row>
        <Col>
          <h3 style={{display: 'inline-block'}}>{I18n.t('general')}</h3>
          {
            !editMode &&
            <Button
              className="pull-right"
              onClick={(e) => { e.preventDefault(); setEditMode(!editMode) }}
            >
              <i className="fa fa-pencil"/>
            </Button>
          }
        </Col>
        <Col size="xs-12" className="mb-1">
          <div><label>{I18n.t('assignees')}</label></div>
          {editMode ?
            <Select
              options={buildAssigneeOptions(formOptions.available_task_assignees, formOptions.available_work_queues)}
              onChange={handleAssigneeSelect}
              value={assignees.map(toAssigneeValue)}
              isMulti
            />
          :
            assignees.length > 0 ? <ReadOnlyMultiSelectValues values={assigneeDetails} /> : <p>{I18n.t('none')}</p>
          }
        </Col>

        { report.report_type == 'payment_confirmation' &&
          <>
            <Col size='xs-12' className='mb-1'>
              <label>{I18n.t('targeted_datagrid_for_payment_confirmation')}</label>
              { editMode ?
                <div>
                  <Select
                    name='targeted_datagrid_id'
                    options={datagridOptions}
                    onChange={(val) => handleTargetedDatagridChange('id', val)}
                    value={targetedDatagrid.id}
                  />
                </div>
                :
                <p>{datagridOptions.find(d => d['value'] == targetedDatagrid.id)?.['label'] || I18n.t('none')}</p>
              }
            </Col>

            <Col size='xs-12' className='mb-1'>
              <label>{I18n.t('target_column_to_update')}</label>
              { editMode ?
                <div>
                  <Select
                    name='targeted_datagrid_col_accessor'
                    options={datagridColOptions}
                    onChange={(val) => handleTargetedDatagridChange('col', val)}
                    value={targetedDatagrid.col}
                  />
                </div>
                :
                <p>{targetedDatagridCol?.['label'] || I18n.t('none')}</p>
              }
            </Col>

            { !targetedDatagrid.loading &&
              <Col size='xs-12' className='mb-1'>
                <label>{I18n.t('new_value_for_column')}</label>
                { editMode ?
                  <div>
                    { targetedDatagridCol?.['selection_options'] ?
                      <Select
                        name='targeted_datagrid_col_value'
                        options={targetedDatagridCol?.['selection_options']}
                        onChange={(val) => handleTargetedDatagridChange('value', val)}
                        value={targetedDatagrid.value}
                      />
                      : ['date', 'timestamp'].includes(targetedDatagridCol?.['data_type']) ?
                        <DateInput
                          name='targeted_datagrid_col_value'
                          defaultValue={targetedDatagrid.value}
                          onChange={(val) => handleTargetedDatagridChange('value', val)}
                        />
                        :
                        <input
                          type='text'
                          name='targeted_datagrid_col_value'
                          defaultValue={targetedDatagrid.value}
                          onChange={(e) => handleTargetedDatagridChange('value', e.target.value)}
                          className='form-control'
                        />
                    }
                  </div>
                  :
                  <p>{targetedDatagrid.value || I18n.t('none')}</p>
                }
              </Col>
            }
          </>
        }

        <Col>
          <hr /><PhaseCollapser isCollapsed={!isAdvancedShown} handleClick={() => setIsAdvancedShown(!isAdvancedShown)} />
        </Col>

        <div className={`${isAdvancedShown ? '' : 'hidden'}`}>
          <Col size="xs-12" className="mb-1">
          <PhaseSectionElement title_key='automatic_final_approval' />
          {editMode ?
            <div>
              <ToggleSwitch
                isOn={automaticApproval}
                handleToggle={() => setAutomaticApproval(!automaticApproval)}
                name='monitoring_report[automatic_approval]'
                yesElem={<TextOptionElem side='left' text={I18n.t('option_yes')} />}
                noElem={<TextOptionElem side='right' text={I18n.t('option_no')} />}
              />
            </div>
            :
            <p>{automaticApproval ? I18n.t('option_yes') : I18n.t('option_no')}</p>
          }
          </Col>

          <Col size="xs-12" className="mb-1" >
            <PhaseSectionElement title_key='confirmation_email_processed_submissions' />
            {editMode ? (
              <div>
                { tenantConfirmEmailProcessedSub ? 
                    <Tooltip title={I18n.t("confirm_email_processed_already_tenant_level")}>
                      <ToggleSwitch
                        isOn={true}
                        disabled={true}
                        yesElem={<TextOptionElem side="left" text={I18n.t("option_yes")} />}
                        noElem={<TextOptionElem side="right" text={I18n.t("option_no")} />}
                      />
                    </Tooltip> 
                    :
                    <ToggleSwitch
                      isOn={confirmEmailProcessedSubmission}
                      handleToggle={() => setConfirmEmailProcessedSubmission(!confirmEmailProcessedSubmission)}
                      name="monitoring_report[confirm_email_processed_submission]"
                      yesElem={<TextOptionElem side="left" text={I18n.t("option_yes")} />}
                      noElem={<TextOptionElem side="right" text={I18n.t("option_no")} />}
                    />
                }
              </div>
            ) : (
                <p>{confirmEmailProcessedSubmission ? I18n.t("option_yes") : I18n.t("option_no")}</p>
              )}
          </Col>

        <Col size="xs-12" className="mb-1">
          <PhaseSectionElement title_key='is_allowed_to_modify_report' />
          {editMode ? (
            <div>
              <ToggleSwitch
                isOn={mayEditReport}
                handleToggle={() => setMayEditReport(!mayEditReport)}
                name="monitoring_report[may_edit_report]"
                disabled={false}
                yesElem={<TextOptionElem side="left" text={I18n.t("option_yes")} />}
                noElem={<TextOptionElem side="right" text={I18n.t("option_no")} />}
              />
            </div>
          ) : (
            <p>{mayEditReport ? I18n.t("option_yes") : I18n.t("option_no")}</p>
          )}
        </Col>
        </div>
      </Row>
      {editMode && <ActionButtons onSave={onSave} onCancel={(e) => { e.preventDefault(); Turbo.visit(location.href); setEditMode(!editMode) }} />}
    </form>
    <FormList {...{report, phase, dispatch, formOptions, availableLocales, onReportUpdate,  rest}}  />
    <div className={`${isAdvancedShown ? '' : 'visibility-hidden'}`}>
      {/* {<SingleForm {...{report, phase, formOptions, availableLocales}} /><hr />} */}
      <PreFillContextList {...{phase, report, dispatch, availablePreFillContexts: formOptions.available_prefill_contexts}} /><hr />
      <FormDataCaptureList {...{phase, report, dispatch}} />
    </div>
  </>
};

export default ApprovalPhaseEdit;

const SingleForm = ({report, phase, formOptions: { available_approval_forms: available_forms }, availableLocales }) => {
  const [formData, setFormData] = useState(() => {
    if (phase.forms && phase.forms[0]) {
      if (!phase.forms[0].name) phase.forms[0].name = {};
      return phase.forms[0];
    }
    return {name: {}, programme_form_id: '', new: true};
  })
  const [editMode, setEditMode] = useState(false);
  const formRef = useRef(null);

  const handleNameChange = e => {
    const { name, value } = e.target;
    setFormData((prev) => ({...prev, name: {...formData.name, [name]: value}}));
  };

  const handleProgrammeFormChange = v => {
    setFormData((prev) => ({...prev, programme_form_id: v}));
  };

  const onSave = (e) => {
    e.preventDefault();
    jQuery.post(monitoring_report_update_form_path({id: report.id, _options: true}), {form: formData, phase_id: phase.id},
      function (data) {
        setEditMode(!editMode);
      })
  };

  const onCancel = (e) => {
    e.preventDefault();
    setEditMode(!editMode);
  };

  const selectedProgrammeForm = available_forms.find((v) => { return v[0] === formData.programme_form_id });

  return (
      <form ref={formRef}>
        <Row>
          <Col>
            <h3 style={{display: 'inline-block'}}>{I18n.t('feedback_form')}</h3>
            {!editMode && (
              <Button
                className='pull-right mt-2'
                onClick={(e) => {
                  e.preventDefault();
                  setEditMode(!editMode);
                }}>
                <i className='fa fa-pencil'/>
              </Button>)
            }
          </Col>

          { availableLocales.map((locale) =>
            <Col size='xs-12' className='mb-1' key={locale}>
              <label>{I18n.t('name')} ({locale})</label>
              {editMode ?
                <input
                  type='text'
                  className='form-control'
                  name={locale}
                  value={formData.name[locale]}
                  onChange={handleNameChange}
                />
                :
                <p>{formData.name[locale] || '-'}</p>
              }
            </Col>)
          }

          <Col size='xs-12' className='mb-1'>
            <div><label>{I18n.t('form')}</label></div>
            {editMode ?
              <Select
                options={available_forms.map(v => ({ value: v[0], label: v[1] }))}
                onChange={handleProgrammeFormChange}
                value={formData.programme_form_id}
                isClearable
              />
              :
              <p>{ (selectedProgrammeForm && selectedProgrammeForm[1]) || '-' }</p>
            }
          </Col>

          {editMode && (
            <ActionButtons
              onSave={onSave}
              onCancel={onCancel}
            />
          )}
        </Row>
      </form>
  );
};
